export namespace Vec2 {

    /// Construct

    export function c(x: number, y: number = x): Vec2 {
        return { x, y };
    }

    export function clone(a: Vec2): Vec2 {
        return { x: a.x, y: a.y };
    }

    /// Math

    export function add(a: Vec2, b: Vec2, ...other: Vec2[]): Vec2 {
        let x = a.x + b.x;
        let y = a.y + b.y;
        for (let o of other) {
            x += o.x;
            y += o.y;
        }

        return { x, y };
    }

    export function sub(a: Vec2, b: Vec2, ...other: Vec2[]): Vec2 {
        let x = a.x - b.x;
        let y = a.y - b.y;
        for (let o of other) {
            x -= o.x;
            y -= o.y;
        }

        return { x, y };
    }

    export function mul(a: Vec2, b: Vec2): Vec2 {
        return { x: a.x * b.x, y: a.y * b.y };
    }

    export function div(a: Vec2, b: Vec2): Vec2 {
        return { x: a.x / b.x, y: a.y / b.y };
    }

    export function floor(a: Vec2): Vec2 {
        return { x: Math.floor(a.x), y: Math.floor(a.y) };
    }

    export function ceil(a: Vec2): Vec2 {
        return { x: Math.ceil(a.x), y: Math.ceil(a.y) };
    }

    export function round(a: Vec2): Vec2 {
        return { x: Math.round(a.x), y: Math.round(a.y) };
    }

    export function scale(a: Vec2, b: number): Vec2 {
        return { x: a.x * b, y: a.y * b };
    }

    export function dot(a: Vec2, b: Vec2): number {
        return a.x * b.x + a.y * b.y;
    }

    export function cross(a: Vec2, b: Vec2): number {
        return a.x * b.y - a.y * b.x;
    }

    export function length(a: Vec2): number {
        return Math.sqrt(a.x * a.x + a.y * a.y);
    }

    export function normalize(a: Vec2): Vec2 {
        const len = length(a);
        if (len == 0) return a;
        return scale(a, 1 / len);
    }

    export function distance(a: Vec2, b: Vec2): number {
        return length(Vec2.sub(a, b));
    }

    export function angle(a: Vec2, b: Vec2): number {
        return Math.acos(dot(a, b) / (length(a) * length(b)));
    }

    export function angleSigned(a: Vec2, b: Vec2): number {
        return Math.atan2(cross(a, b), dot(a, b));
    }

    export function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }

    export function equals(a: Vec2, b: Vec2): boolean {
        return a.x == b.x && a.y == b.y;
    }

    export function min(a: Vec2, b: Vec2): Vec2 {
        return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) };
    }

    export function max(a: Vec2, b: Vec2): Vec2 {
        return { x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) };
    }

    export function clamp(a: Vec2, min: Vec2, max: Vec2): Vec2 {
        return Vec2.min(Vec2.max(a, min), max);
    }

    export function abs(a: Vec2): Vec2 {
        return { x: Math.abs(a.x), y: Math.abs(a.y) };
    }

    export function sign(a: Vec2): Vec2 {
        return { x: Math.sign(a.x), y: Math.sign(a.y) };
    }

    export function negate(a: Vec2): Vec2 {
        return { x: -a.x, y: -a.y };
    }

    export function inverse(a: Vec2): Vec2 {
        return { x: 1 / a.x, y: 1 / a.y };
    }

    export function mirror(source: Vec2, target: Vec2) {
        const delta = Vec2.sub(target, source);
        return Vec2.sub(source, delta);
    }

    export function center(vecs: Vec2[]) {
        let x = 0;
        let y = 0;
        for (let vec of vecs) {
            x += vec.x;
            y += vec.y;
        }
        x /= vecs.length;
        y /= vecs.length;

        return { x, y };
    }

    /// Create

    export function fromArray(a: number[]): Vec2 {
        return { x: a[0], y: a[1] };
    }

    export function fromEvent(e: { clientX: number; clientY: number }): Vec2 {
        return { x: e.clientX, y: e.clientY };
    }

    export function fromBoundsPosition(e: Element): Vec2 {
        const bounds = e.getBoundingClientRect();
        return { x: bounds.left, y: bounds.top };
    }

    export function fromBoundsSize(e: Element): Vec2 {
        const bounds = e.getBoundingClientRect();
        return { x: bounds.width, y: bounds.height };
    }

    /**
     * Get the padding & border (x: left, y: top) of an element
     */
    export function fromPadding(e: Element): Vec2 {
        const style = getComputedStyle(e);
        return {
            x: parseFloat(style.paddingLeft) + parseFloat(style.borderLeft),
            y: parseFloat(style.paddingTop) + parseFloat(style.borderTop),
        };
    }

    /// Convert

    export function toString(a: Vec2): string {
        return `(${a.x}, ${a.y})`;
    }

    export function toArray(a: Vec2): [number, number] {
        return [a.x, a.y];
    }

    export function toCss(a: Vec2, prefix: string = "")  {
        return {
            [`${prefix}x`]: `${a.x}px`,
            [`${prefix}y`]: `${a.y}px`,
        };
    }

    // constants
    export const zero = () => ({ x: 0, y: 0 }) as Vec2;
    export const one = () => ({ x: 1, y: 1 }) as Vec2;
    export const infinite = () => ({ x: Infinity, y: Infinity }) as Vec2;
    export const negativeInfinite = () => ({ x: -Infinity, y: -Infinity }) as Vec2;
}
